import React from 'react';
import { BrowserRouter as Router, Navigate, Route, Routes, useNavigate } from 'react-router-dom';
import AdminRoute from './components/auth/AdminRoute';

import { AuthProvider } from './components/auth/AuthContext';
import { LoadingProvider } from './contexts/LoadingContext';
import { SearchDrawerProvider, useSearchDrawer } from './contexts/SearchDrawerContext';
import { NotificationProvider } from './contexts/NotificationContext';
import { MobileNavProvider } from './contexts/MobileNavContext';
import { UserSettingsProvider } from './contexts/UserSettingsContext';
import SearchDrawer from './components/drawers/SearchDrawer';
import AppWrapper from './components/AppWrapper';
import CustomToastContainer from './components/notifications/CustomToastContainer';
import ApplicationStatusNotifier from './components/notifications/ApplicationStatusNotifier';
import RetryContactNotifier from './components/notifications/RetryContactNotifier';
import RecommendedActionNotifier from './components/notifications/RecommendedActionNotifier';
import ContactStatusNotifier from './components/notifications/ContactStatusNotifier';
import OpportunitiesReadyNotifier from './components/notifications/OpportunitiesReadyNotifier';
import Login from './pages/signIn/SignIn';
import ForgotPassword from './pages/forgotPassword/ForgotPassword';
import ResetPassword from './pages/resetPassword/ResetPassword';
import CreateAccount from './pages/registration/createAccount/CreateAccount';
import AccountCreated from './pages/registration/accountCreated/AccountCreated';
import VerifyEmail from './pages/registration/verifyEmail/VerifyEmail';
import ConfirmationThankYou from './pages/registration/confirmationThankYou/ConfirmationThankYou';
import AdminShell from './pages/admin/AdminShell';
import { ADMIN_INDEX_PATH, ADMIN_ROUTES, adminChildPath } from './pages/admin/adminNav';

import Dashboard from './pages/dashboard/Dashboard';
import Searches from './pages/searches/Searches';
import Opportunities from './pages/opportunities/Opportunities';
import Applications from './pages/applications/Applications';
import Archive from './pages/archive/Archive';
import Profile from './pages/profile/Profile';
import Settings from './pages/settings/Settings';
import ResumeTailor from './pages/resumeTailor/ResumeTailor';
import EmailImportAttach from './pages/emailImports/EmailImportAttach';
import SideMenu from './components/layout/SideMenu';
import Header from './components/layout/Header';
import Logo from './components/layout/Logo';
import PrivateRoute from './components/auth/PrivateRoute';
import LoadingOverlay from './components/spinner/LoadingOverlay';
import PageViewMirror from './components/analytics/PageViewMirror';

import './App.scss';

const SearchDrawerWrapper: React.FC = () => {
  const { isOpen, closeDrawer, drawerClosed, notifySearchCreated } = useSearchDrawer();
  const navigate = useNavigate();

  const handleSuccess = (ids: string[]) => {
    notifySearchCreated(ids);
    navigate('/searches');
  };

  return (
    <SearchDrawer
      open={isOpen}
      onClose={closeDrawer}
      onSuccess={handleSuccess}
      onAfterClose={drawerClosed}
    />
  );
};

const App: React.FC = () => {
  return (
    <AuthProvider>
      <LoadingProvider>
        <Router>
          {/*
            Inside <Router> because it reads useLocation(). Above <Routes>
            because it must record the navigation whether or not the route that
            follows renders — an AdminRoute redirect or a PrivateRoute bounce is
            itself a page view worth having in the log. Its position among the
            providers is not load-bearing; it consumes none of them.
          */}
          <PageViewMirror />
          <UserSettingsProvider>
          <NotificationProvider>
          <SearchDrawerProvider>
          <MobileNavProvider>
            <AppWrapper>
              <Routes>
                <Route path="/signin" element={<Login />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/reset-password" element={<ResetPassword />} />
                <Route path="/registration/create-account" element={<CreateAccount />} />
                <Route path="/registration/account-created" element={<AccountCreated />} />
                <Route path="/registration/verify-email" element={<VerifyEmail />} />
                <Route path="/registration/confirmation-thank-you" element={<ConfirmationThankYou />} />
                <Route path="/email-imports/attach" element={<EmailImportAttach />} />
                {/*
                  Nested rather than a second <Routes>: AdminRoute wraps the
                  PARENT element, so every console surface below is gated by the
                  same check — a child only ever renders through the layout's
                  <Outlet>, which never mounts when the guard redirects.

                  The children are generated from ADMIN_ROUTES so the paths the
                  router serves and the paths the sidebar links to are the same
                  strings, not two lists that agree today.
                */}
                <Route
                  path="/admin"
                  element={
                    <AdminRoute
                      // The chrome, the range provider and the boundary that
                      // covers the console's own chunk. Extracted so that
                      // arrangement is testable rather than restated by a test.
                      element={<AdminShell />}
                    />
                  }
                >
                  <Route index element={<Navigate to={ADMIN_INDEX_PATH} replace />} />
                  {ADMIN_ROUTES.map((entry) => (
                    <Route
                      key={entry.path}
                      path={adminChildPath(entry)}
                      element={<entry.Component />}
                    />
                  ))}
                </Route>
                <Route
                  path="/*"
                  element={
                    <PrivateRoute
                      element={
                        <>
                          <Logo />
                          <SideMenu />
                          <Header />
                          <main className="main-content">
                            <Routes>
                              <Route path="/" element={<Dashboard />} />
                              <Route path="/searches" element={<Searches />} />
                              <Route path="/searches/:id" element={<Searches />} />
                              <Route path="/resume/tailor/:versionId" element={<ResumeTailor />} />
                              <Route path="/opportunities/*" element={<Opportunities />} />
                              <Route path="/applications" element={<Applications />} />
                              <Route path="/applications/:id" element={<Applications />} />
                              <Route path="/archive" element={<Archive />} />
                              <Route path="/archive/:id" element={<Archive />} />
                              <Route path="/profile" element={<Profile />} />
                              <Route path="/settings" element={<Settings />} />
                            </Routes>
                          </main>
                        </>
                      }
                    />
                  }
                />
              </Routes>
              <LoadingOverlay />
            </AppWrapper>
          </MobileNavProvider>
            <SearchDrawerWrapper />
            <ApplicationStatusNotifier />
            <RetryContactNotifier />
            <RecommendedActionNotifier />
            <ContactStatusNotifier />
            <OpportunitiesReadyNotifier />
            <CustomToastContainer />
          </SearchDrawerProvider>
          </NotificationProvider>
          </UserSettingsProvider>
        </Router>
      </LoadingProvider>
    </AuthProvider>
  );
};

export default App;
